import { useEffect, useState } from "react";
import BrownsvilleHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Brownsville?", a: "We offer Brownsville-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit an office in Brownsville?", a: "No. We do not have a physical office in Brownsville. Our service is remote: you complete forms online, visit a local acceptance facility (like a Brownsville post office or Cameron County Clerk) for in-person verification, then ship documents to us via overnight courier. We handle the expedited submission at the State Department and return your passport to your Brownsville address." },
  { q: "Where is the nearest passport acceptance facility in Brownsville?", a: "Brownsville has several acceptance facilities including the downtown post office, Cameron County Clerk offices, and select locations in Harlingen and San Benito. We provide a curated list of Brownsville-area facilities with address, phone, and photo availability. First-time applicants and minors must visit in person; renewals typically ship directly." },
  { q: "Can I get a same-day passport in Brownsville?", a: "The nearest passport agency is in Houston, which does not accept walk-ins without proof of imminent travel within 14 days and an appointment. Same-day processing is extremely rare. For faster-than-14-day needs, our registered courier service offers the fastest legally available channel — as quick as 24 hours with proper documentation." },
  { q: "How much does expedited passport service cost in Brownsville?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
  { q: "What documents do I need for a Brownsville passport application?", a: "For a new adult passport: proof of citizenship (certified birth certificate or naturalization certificate), government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear (or provide consent), plus the child's birth certificate and photos. We provide a detailed checklist for Brownsville clients." },
  { q: "Can I get a passport photo in Brownsville?", a: "Yes. Many Brownsville post offices, CVS, Walgreens, and Walmart locations offer passport photos. Some post offices include photo services with their acceptance facility hours. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications — 2x2 inches, white background, no glasses, neutral expression." },
  { q: "Do I need an appointment at Brownsville passport acceptance facilities?", a: "Many Brownsville post offices require appointments for passport acceptance. Some accept walk-ins during limited hours. We recommend calling ahead. Cameron County Clerk offices and larger post offices typically offer more flexible appointment times. We can help you find the facility with the next available appointment." },
  { q: "Can I track my Brownsville passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage: from Brownsville to our processing center, through State Department review, and return shipping to your Texas address." },
  { q: "What if my passport is lost or stolen in Brownsville?", a: "File a police report if stolen, then complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. If you have upcoming travel, our expedited service can process the replacement as fast as 24 hours." },
];

function BrownsvilleStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function BrownsvilleProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Brownsville Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Brownsville-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Brownsville travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For business travelers and families with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Brownsville residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrownsvilleServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Brownsville travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Brownsville residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Brownsville adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Brownsville residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Brownsville families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent Brownsville business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Brownsville applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Brownsville Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Brownsville's diverse travel community — from port workers to students to families visiting relatives across the border.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrownsvilleLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Brownsville</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Brownsville sits at the southernmost tip of Texas, where the Rio Grande meets the Gulf of Mexico. It is a city of 190,000 people with deep historical roots, a vibrant binational culture, and an economy that spans international trade, education, and increasingly, aerospace. Brownsville is not just a border town — it is the gateway to the Rio Grande Valley and the launching point for SpaceX's next-generation space exploration.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Port of Brownsville is one of the largest deep-water ports on the Gulf Coast, handling millions of tons of cargo annually. The port's international shipping connections create consistent demand for passports among port workers, maritime contractors, and logistics professionals who travel to meet clients, inspect vessels, or coordinate operations across Latin America and beyond. A last-minute assignment to Panama, Colombia, or the Caribbean can require a passport renewal with days, not weeks, to spare.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            SpaceX's Boca Chica launch site, just 20 miles east of Brownsville, has transformed the region. Hundreds of engineers, technicians, and contractors now call Brownsville and the surrounding Rio Grande Valley home. These aerospace professionals frequently travel to SpaceX headquarters in Hawthorne, California, to international suppliers in Europe, and to launch sites worldwide. The sudden nature of aerospace deployment — a launch window shifted, a critical component needing inspection — creates some of the most time-sensitive passport demand in the state.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Brownsville's proximity to Matamoros, Mexico, creates a unique cross-border lifestyle. Many families have relatives on both sides of the river. Weekend visits, family celebrations, medical appointments, and cross-border shopping are routine. But the passport requirements for land crossings, and the growing complexity of border documentation, mean that passport validity is a constant concern. A family emergency in Mexico can require a valid passport for the drive across the Veterans International Bridge — and an expired passport can turn a 10-minute crossing into a multi-day bureaucratic nightmare.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Texas Southmost College and the University of Texas Rio Grande Valley serve thousands of students from Brownsville and surrounding communities. Study abroad programs, research conferences, and international internships create passport demand among young people who may be applying for their first passport. The compressed timelines of academic travel — a semester abroad confirmed in August for a September departure, a conference abstract accepted in March for a June conference — require expedited processing that standard post office channels cannot match.
          </p>
        </div>
      </div>
    </section>
  );
}

function BrownsvilleTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Brownsville Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">BRO — Brownsville/South Padre Island International Airport</h3>
                <p className="text-gray-400 text-xs">Eastern Brownsville, connecting to Dallas, Houston, and Mexico</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              BRO provides essential air connectivity for Brownsville residents, with flights to Dallas, Houston, and seasonal service to Mexico City. For Brownsville travelers, getting to an international flight typically requires connecting through DFW or IAH. The connection time adds complexity to already-tight passport timelines — a flight to London with a 2-hour connection in Dallas means your passport needs to be ready before you leave Brownsville, not when you arrive at your final destination.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> International flights from Brownsville require at least one connection. Check your passport validity before booking — connection airports like DFW and IAH have no margin for document errors.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-road-map-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Veterans International Bridge &amp; Cross-Border Travel</h3>
                <p className="text-gray-400 text-xs">Connecting Brownsville to Matamoros, Mexico</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Veterans International Bridge is one of the busiest land crossings on the U.S.-Mexico border. Brownsville families cross daily for work, family visits, medical appointments, and shopping. While passport cards work for land crossings, many families discover they need passport books for air travel to visit extended family in Mexico's interior. The proximity of the border creates a false sense of security — passports are needed for air travel even for short flights to Monterrey or Mexico City.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If your family in Mexico requires a flight (not just a drive), you need a passport book. The card is only valid for land and sea crossings. Verify your travel requirements before applying.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Port of Brownsville &amp; Maritime Travel</h3>
                <p className="text-gray-400 text-xs">One of the largest deep-water ports on the Gulf Coast</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Port of Brownsville handles over 10 million tons of cargo annually and employs thousands of maritime professionals. Port workers, ship brokers, and logistics coordinators frequently travel to meet international clients, inspect vessels, or coordinate cargo operations. These trips often have short notice — a vessel arriving early, a client needing immediate inspection, or a contract requiring in-person negotiation. The nearest passport agency is hours away in Houston, making local expedited service essential.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Maritime work often requires multiple-entry visas. Your passport needs sufficient blank pages and validity for visa stamps. A full passport book is often better than a card for international port workers.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-rocket-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">SpaceX Boca Chica &amp; Aerospace Travel</h3>
                <p className="text-gray-400 text-xs">20 miles east of Brownsville, SpaceX Starbase</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              SpaceX's Boca Chica facility has brought hundreds of aerospace engineers and technicians to the Brownsville area. These professionals travel frequently to SpaceX headquarters in California, to international suppliers in Germany and France, and to launch sites worldwide. The aerospace industry's rapid pace means travel schedules can change overnight — a launch window moved, a critical component needing immediate inspection, a supplier meeting pushed up. The nearest passport agency is hours away, making expedited service critical for Brownsville's aerospace community.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> SpaceX employees frequently travel on short notice. Keep your passport valid for at least 6 months beyond any potential travel dates. Many aerospace suppliers require Schengen visas with current passport validity.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-graduation-cap-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Texas Southmost College &amp; UTRGV Students</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Brownsville's colleges serve thousands of students who participate in study abroad, international research, and cross-border academic programs. From nursing students doing clinical rotations in Mexico to engineering students attending conferences in Europe, academic travel creates unique passport timing pressures. We process student passports with the urgency and affordability that students need.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function BrownsvilleDestinationsSection() {
  const destinations = [
    { name: "Mexico", context: "Cross-border family visits, medical tourism in Monterrey, heritage trips to Mexico City, Guadalajara, and Oaxaca", urgency: "Family emergencies often require immediate travel across the border or to Mexico's interior", icon: "ri-map-pin-2-line", flag: "MX" },
    { name: "Central America", context: "Port business in Panama, Costa Rica tourism, cruise departures from nearby ports", urgency: "Cruise bookings and port assignments often have 2–4 week notice", icon: "ri-map-pin-2-line", flag: "PA" },
    { name: "Caribbean", context: "Cruise vacations from Galveston or Miami, resort travel, family beach trips", urgency: "Spring break and summer cruise bookings peak February through June", icon: "ri-map-pin-2-line", flag: "BS" },
    { name: "United Kingdom &amp; Europe", context: "SpaceX supplier visits, aerospace conferences, study abroad programs", urgency: "Business meetings and conference schedules have fixed, immovable dates", icon: "ri-map-pin-2-line", flag: "GB" },
    { name: "South America", context: "Port business in Colombia, Brazil, and Argentina; family heritage in South America", urgency: "Energy and maritime contracts often require 7–14 day deployment", icon: "ri-map-pin-2-line", flag: "CO" },
    { name: "California", context: "SpaceX headquarters travel, aerospace industry meetings, family visits", urgency: "Internal SpaceX travel can be arranged with 2–3 days notice", icon: "ri-map-pin-2-line", flag: "US" },
  ];
  const commonSituations = [
    { stat: "35%", label: "of Brownsville expedited requests are cross-border family or emergency travel to Mexico" },
    { stat: "2–4 Weeks", label: "average notice Brownsville travelers have before international departure or cruise sailing" },
    { stat: "Feb–Jun", label: "peak season when spring break, summer cruises, and academic travel coincide" },
    { stat: "72 Hours", label: "shortest turnaround we have accommodated for a Brownsville port worker deploying to Panama" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Brownsville Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/mcallen" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">McAllen <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/laredo" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Laredo <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/corpus-christi" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Corpus Christi <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrownsvilleTestimonialsSection() {
  const testimonials = [
    { name: "Carlos M.", location: "Downtown Brownsville", text: "My mother in Monterrey needed surgery and I had to fly there in 5 days. My passport was expired. Fast Passport Center got it to me in 3 days. I was able to be there for her.", rating: 5 },
    { name: "Jennifer L.", location: "Port of Brownsville", text: "I had a last-minute vessel inspection in Panama with 10 days notice. They processed my passport renewal in 4 days. The document review caught a photo issue I would have missed.", rating: 5 },
    { name: "The Garcia Family", location: "Olmito", text: "We booked a cruise out of Galveston and realized our daughter's passport expired. They helped us through the entire process and got it back in 7 days. Our vacation was saved.", rating: 5 },
    { name: "Dr. Ahmed R.", location: "UTRGV Brownsville", text: "My conference in Barcelona was in 3 weeks and my passport had a damaged page. They handled the replacement and got it back with time to spare. Professional and thorough.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Brownsville Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrownsvilleAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Brownsville Main Post Office", address: "4550 E 14th St, Brownsville, TX 78521", phone: "(956) 542-7102", photo: "Yes", appointment: "Required" },
    { name: "Cameron County District Clerk", address: "964 E Harrison St, Brownsville, TX 78520", phone: "(956) 544-0815", photo: "No", appointment: "Required" },
    { name: "Harlingen Post Office", address: "310 E Tyler Ave, Harlingen, TX 78550", phone: "(956) 423-6367", photo: "Yes", appointment: "Walk-in" },
    { name: "San Benito Post Office", address: "425 W Stenger St, San Benito, TX 78586", phone: "(956) 399-5210", photo: "Yes", appointment: "Walk-in" },
    { name: "Port Isabel Post Office", address: "104 W Hickman Ave, Port Isabel, TX 78578", phone: "(956) 943-1360", photo: "Yes", appointment: "Required" },
    { name: "South Padre Island Post Office", address: "4101 Padre Blvd, South Padre Island, TX 78597", phone: "(956) 761-4583", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Brownsville Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Brownsville-area options with hours and contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability. We can also help you find the facility with the earliest appointment slot.</p>
        </div>
      </div>
    </section>
  );
}

function BrownsvilleComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Brownsville Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "Brownsville-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function BrownsvilleMistakesSection() {
  const mistakes = [
    { title: "Assuming the Border Card is Enough", desc: "Many Brownsville residents use passport cards for land crossings but need passport books for international flights. Booking a flight to Mexico City with only a card means you're stuck at the airport." },
    { title: "Waiting Until the Last Minute", desc: "Brownsville's post office appointments fill up fast, especially during spring break and summer. Waiting until 2 weeks before travel often leaves you with no available appointment slots." },
    { title: "Using the Wrong Form", desc: "Brownsville applicants frequently confuse DS-11 (new/child) with DS-82 (renewal). Using the wrong form causes rejection and delays your entire timeline." },
    { title: "Unacceptable Passport Photos", desc: "Many Brownsville CVS and Walgreens photo technicians are not trained on current State Department photo standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Not Checking Family Passport Validity", desc: "Brownsville families often cross the border regularly and forget to check expiration dates. A child's passport can expire without notice, grounding the entire family for a planned trip." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Brownsville Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrownsvilleHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our Brownsville-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Brownsville-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Brownsville address via secure overnight shipping. Most Brownsville clients receive it within their chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Brownsville Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrownsvilleFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Brownsville Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrownsvilleHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Brownsville Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function BrownsvilleFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Brownsville Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you are flying to Mexico City in 3 days, sailing on a Caribbean cruise in 3 weeks, or deploying to a SpaceX supplier in Europe, we will get your passport processed on time. No surprises. No delays. Just fast, reliable service.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function BrownsvillePage() {
  useEffect(() => {
    document.title = "Emergency Passport Brownsville, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/brownsville/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Brownsville, TX — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Brownsville port workers, SpaceX employees, students, and cross-border families.");
    setMeta("geo.placename", "Brownsville, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "25.9014;-97.4975");
    setMeta("ICBM", "25.9014, -97.4975");
    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for Brownsville travelers with immediate departure needs." },
      { name: "Passport Renewal", description: "Fast passport renewal for Brownsville residents with recent passport issuance." },
      { name: "New Passport", description: "First-time application assistance for Brownsville adults and students." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for Brownsville residents." },
      { name: "Child Passport", description: "Passport services for minors under 16 in Brownsville." },
      { name: "Name Change", description: "Passport correction after marriage, divorce, or legal name changes." },
      { name: "Second Passport", description: "Second passport issuance for frequent Brownsville business travelers." },
    ];
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Brownsville Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Fast Passport Center does not have a physical office in Brownsville — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Brownsville and the Rio Grande Valley.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "Brownsville", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      ...servicesList.map((s) => ({ "@type": "Service", serviceType: s.name, provider: { "@type": "LocalBusiness", name: "Fast Passport Center" }, areaServed: { "@type": "City", name: "Brownsville" }, description: s.description })),
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <BrownsvilleHeroSection />
      <BrownsvilleStickyNav />
      <BrownsvilleProcessingTimeSection />
      <BrownsvilleServicesSection />
      <BrownsvilleLocalAreaSection />
      <BrownsvilleTravelTimingSection />
      <BrownsvilleDestinationsSection />
      <BrownsvilleTestimonialsSection />
      <BrownsvilleAcceptanceFacilitiesSection />
      <BrownsvilleComparisonSection />
      <BrownsvilleMistakesSection />
      <BrownsvilleHowItWorksSection />
      <BrownsvilleFAQSection />
      <BrownsvilleHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/brownsville" state="TX" />
      <BrownsvilleFinalCTA />
    </div>
  );
}